import React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useTranslation } from "react-i18next"
import { useLocation } from "@reach/router"
import {
  getRegionPathV2,
  removeTrailingSlash,
  regionWithDefaultLang,
} from "@/utils"
import {
  routesMap,
  getRegionLocaleFromRoute,
  EEA_HREFLANG,
} from "@/configs/routes"
import { allPages, dynamicPages } from "@/configs/pages"
import { getNavigatePathV2 } from "@/modules/NavBar/utils"
import { Route } from "@/configs/region"
import { metadata } from "@/configs/metadata"

const defaultMetaImgSrc = "https://crypto.com/images/meta-og/listing.png"

const getAlternateLinks = (navigatePath: string, xDefaultPath: string) => {
  const links: JSX.Element[] = []

  const truncatedPath = navigatePath === "/" ? "" : `/${navigatePath}` // for homepage, do not add extra '/'
  const currentPage = allPages.find(page => page.path === truncatedPath)
  const dynamicPage = dynamicPages.find(page =>
    truncatedPath.includes(page.path)
  )
  // ✅ Generate links only for available regions
  if (currentPage) {
    // ✅ Generate links only for available regions
    currentPage.availRegions.forEach(region => {
      const regionConfig = routesMap[region as keyof typeof routesMap]
      if (regionConfig) {
        regionConfig.regionURL.forEach(route => {
          if (route === "eea") {
            EEA_HREFLANG.forEach(hrefLang => {
              const href = `https://crypto.com/eea${truncatedPath}`
              links.push(
                <link
                  key={`alternate-${hrefLang}`}
                  rel="alternate"
                  hrefLang={hrefLang}
                  href={href}
                />
              )
            })
          } else {
            // Find corresponding language for this route
            const { locale, hrefLang } = getRegionLocaleFromRoute(
              route as Route
            )
            if (locale && hrefLang) {
              const href = `https://crypto.com/${route}${truncatedPath}`
              links.push(
                <link
                  key={`alternate-${locale}`}
                  rel="alternate"
                  hrefLang={hrefLang}
                  href={href}
                />
              )
            }
          }
        })
      }
    })

    // ✅ Use page's xDefault
    links.push(
      <link
        key="alternate-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={currentPage.xDefault}
      />
    )
  } else if (dynamicPage) {
    dynamicPage.availRegions.forEach(region => {
      const regionConfig = routesMap[region as keyof typeof routesMap]
      if (regionConfig) {
        regionConfig.regionURL.forEach(route => {
          if (route === "eea") {
            EEA_HREFLANG.forEach(hrefLang => {
              const href = `https://crypto.com/eea${truncatedPath}`
              links.push(
                <link
                  key={`alternate-${hrefLang}`}
                  rel="alternate"
                  hrefLang={hrefLang}
                  href={href}
                />
              )
            })
          } else {
            // Find corresponding language for this route
            const { locale, hrefLang } = getRegionLocaleFromRoute(
              route as Route
            )
            if (locale && hrefLang) {
              const href = `https://crypto.com/${route}${truncatedPath}`
              links.push(
                <link
                  key={`alternate-${locale}`}
                  rel="alternate"
                  hrefLang={hrefLang}
                  href={href}
                />
              )
            }
          }
        })
      }
    })

    // ✅ Use page's xDefault
    links.push(
      <link
        key="alternate-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={dynamicPage.xDefault + navigatePath}
      />
    )
  } else {
    // ✅ Fallback: individual pages ,cannot change region , language
    const href = `https://crypto.com${navigatePath}`
    links.push(
      <link key={`alternate-en`} rel="alternate" hrefLang={"en"} href={href} />
    )

    // Use provided xDefaultPath as fallback
    links.push(
      <link
        key="alternate-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={href}
      />
    )
  }

  return links
}

const faqSchema = {
  "@context": "https://schema.org/",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Cryptocurrency is a digital or virtual currency that operates on distributed ledger technology called a blockchain and uses cryptography for security. It is decentralised and operates independently of a central bank. Unlike traditional currencies, cryptocurrencies are not backed by a physical commodity or government, and their value is determined by market demand and supply. Cryptocurrencies can be used to buy goods and services, transfer funds, and trade in markets. Popular cryptocurrencies include Bitcoin, Ethereum, Litecoin, Ripple, and Cronos. Many cryptocurrencies, like Bitcoin, are created through a process called mining, which involves solving complex mathematical equations to validate and record transactions on a blockchain. This mechanism is also called Proof of Work (PoW). Another consensus mechanism that has increased in popularity — as it is more energy efficient — is Proof of Stake (PoS). Instead of mining, PoS relies on network participants validating transactions. Ethereum, the second-largest cryptocurrency, uses this consensus mechanism.",
      },
    },
    {
      "@type": "Question",
      name: "Where to buy crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "There are several ways to buy cryptocurrencies, including: Brokerage services: Crypto brokers allow users to simply buy and sell cryptocurrencies. A popular example is the Crypto.com App, trusted by over 150 million users. It is available on the Apple Store and Google Play. Cryptocurrency exchanges: These are online platforms where users can buy, sell, and trade cryptocurrencies using fiat currency or other cryptocurrencies. They offer more complex functions compared to a crypto brokerage, adding trading instruments like crypto derivatives. The Crypto.com Exchange is an example of a popular crypto exchange. Peer-to-peer (P2P) marketplaces: These are platforms where buyers and sellers can directly trade cryptocurrencies without the involvement of a third-party exchange. This is also known as DeFi, short for decentralised finance. Multiple P2P crypto marketplaces can be accessed all in one app via the Crypto.com DeFi Wallet. It is important to perform proper research and choose a reputable platform to buy cryptocurrencies. For instance, Crypto.com holds the highest security rating in the industry. In addition, it is advisable to store cryptocurrencies securely in a wallet like the Crypto.com App or Crypto.com DeFi Wallet.",
      },
    },
    {
      "@type": "Question",
      name: "How to buy crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "To buy crypto, follow these general steps:\nChoose a crypto platform to use, like the Crypto.com Exchange or Crypto.com App.\nCreate an account on the chosen platform by providing personal information and ID verification, also known as 'Know Your Customer' (KYC) procedures.\nDeposit fiat currency or another cryptocurrency into the newly created account. The Crypto.com App supports bank transfers, credit cards, debit cards, and cryptocurrency transfers to buy crypto, depending on region.\nNavigate to the 'Buy' section of the Crypto.com Exchange or App and select the crypto to buy.\nEnter the amount of cryptocurrency to buy and confirm the transaction.\nThe crypto will be deposited into the account. From here, it can be transferred to other crypto wallets or converted back to fiat currency and paid out to a bank account.\nIt is important to perform proper research and choose a reputable platform to buy cryptocurrencies. For instance, Crypto.com holds the highest security rating in the industry. In addition, it is advisable to store cryptocurrencies securely in a wallet like the Crypto.com App or Crypto.com DeFi Wallet.",
      },
    },
    {
      "@type": "Question",
      name: "How to trade crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "To trade cryptocurrency, follow these general steps:\nChoose a cryptocurrency exchange that supports trading. A popular option is the Crypto.com Exchange.\nCreate an account on the chosen platform and perform ID verification, known as KYC ('Know Your Customer').\nDeposit funds into the newly created account using a supported payment method. The Crypto.com Exchange supports bank transfers and credit/debit cards.\nNavigate to the trading section of the platform and select the cryptocurrency pair to trade.\nChoose whether to buy or sell the cryptocurrency and enter the amount to trade.\nSet the preferred price and order type. There are several types of orders, including market orders, limit orders, stop orders, and crypto options, which allow users to buy or sell at a specific price or under certain conditions.\nSubmit the trade order and wait for it to be executed. Depending on market conditions, the trade may be filled immediately, or it may take time to be filled.\nMonitor trades and adjust strategies as necessary.\nHere is an introduction to trading on the Crypto.com Exchange.\nIt is crucial to note that trading cryptocurrency carries risk, and it is important to trade only what you can afford to lose.",
      },
    },
    {
      "@type": "Question",
      name: "How to earn crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "There are several ways to earn cryptocurrency, including:\nMining: Cryptocurrency mining involves using specialized computer hardware to solve complex mathematical equations that validate transactions on a blockchain network. Successful miners are rewarded with newly minted cryptocurrency for their efforts.\nStaking/Lockups: Staking and lockups involve holding or locking up a certain amount of cryptocurrency in a wallet or on a platform to support the operations of the blockchain network. Stakers are rewarded with new cryptocurrency as a form of interest for their support.\nTrading: Trading cryptocurrency involves buying and selling cryptocurrencies on exchanges or other trading platforms. Those who have a good understanding of market trends and are able to make informed trading decisions can earn profits through trading.\nAirdrops: Airdrops are free distributions of cryptocurrency to users who meet certain criteria or participate in promotional activities.\nCrypto Projects: Some blockchain projects offer rewards or bounties for users who contribute to their development or community. This can include activities like bug bounties, testing, or content creation.\nIt's important to note that each method of earning cryptocurrency carries its own risks and rewards. It is recommended to carefully research cryptocurrencies and understand the process before buying. Learn more about the crypto market at Crypto.com University.",
      },
    },
    {
      "@type": "Question",
      name: "How to earn rewards on crypto?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Users can earn rewards on their cryptocurrency holdings through various products that offer rewards-bearing accounts or lending services. Here are some ways to earn rewards on cryptocurrency:\nThe Crypto.com App, Exchange, and DeFi Wallet all offer different ways to earn rewards on crypto, called Crypto Earn.\nCreate an account on the chosen platform and deposit cryptocurrency holdings into the rewards account.\nDepending on the platform, users may earn rewards through lending their cryptocurrency to other users or by locking up their cryptocurrency for a period of time.\nSome products offer fixed reward rates (e.g., the Crypto.com Visa Card), while others may offer variable rates that depend on market conditions (e.g., the 'Earn' feature in the Crypto.com DeFi Wallet).\nMonitor rewards and adjust strategies as necessary.\nIt's important to note that earning interest and rewards on cryptocurrency carries risks, including fluctuations in market conditions that may affect interest rates. Make sure to carefully research and understand terms and conditions before depositing cryptocurrency.",
      },
    },
  ],
}

const Seo = ({
  //lang = "en",
  meta = [],
  page,
  title,
  description,
  hasImageSrc = false,
  imageWidth = 2500,
  imageHeight = 1309,
  hasFAQSchema = false,
  customFAQSchema,
  isNoIndex = false,
  maxAPR = 0,
}: {
  //lang?: string
  meta?: { [key: string]: string }[]
  page?: string
  title?: string
  description?: string
  hasImageSrc?: boolean
  imageWidth?: number
  imageHeight?: number
  hasFAQSchema?: boolean
  customFAQSchema?: any
  isNoIndex?: boolean
  maxAPR?: number
}) => {
  const { t, i18n } = useTranslation("metadata")
  const location = useLocation()
  const { site } = useStaticQuery(query)
  const region = getRegionPathV2(location.pathname)
  const isGlobal = region === "" ? true : false
  const isUS = region === "us" ? true : false
  const isKR = region === "kr" ? true : false
  const isSG = region === "sg" ? true : false
  const lang = (i18n.language ?? "en") as string
  //let canonicalHref = getNavigatePathV2(location.pathname)
  let canonicalHref = "https://crypto.com" + location.pathname
  canonicalHref = removeTrailingSlash(canonicalHref)
  const navigatePath = getNavigatePathV2(location.pathname)
  const xDefaultPath = "https://crypto.com/en" + navigatePath

  const tFuncLang = regionWithDefaultLang(region)

  let metaDescription =
    description ?? t(`${page}.description`, { lng: tFuncLang })

  let metaTitle = title ?? t(`${page}.title`, { lng: tFuncLang })

  if (page === "home" || page === "app") {
    const route = location.pathname.split("/")[1]
    if (route && metadata[route as keyof typeof metadata]) {
      metaTitle = metadata[route as keyof typeof metadata][page].title
      metaDescription =
        metadata[route as keyof typeof metadata][page].description
    }
  }

  if (page === "fftb" && isUS) {
    metaDescription = t("fftb.description_us", { lng: tFuncLang })
    metaTitle = t("fftb.title_us", { lng: tFuncLang })
  }

  if (page === "staking") {
    metaTitle = t("staking.title_new", { maxAPR })
  }

  if (page === "security") {
    metaDescription = t("security.description_1", { lng: tFuncLang })
  }

  const imageMeta: React.DetailedHTMLProps<
    React.MetaHTMLAttributes<HTMLMetaElement>,
    HTMLMetaElement
  >[] = [
    {
      name: `twitter:image`,
      content: hasImageSrc ? t(`${page}.image`) : defaultMetaImgSrc,
    },
    {
      property: `og:image`,
      content: hasImageSrc ? t(`${page}.image`) : defaultMetaImgSrc,
    },
    {
      property: `og:image:width`,
      content: `${imageWidth}`,
    },
    {
      property: `og:image:height`,
      content: `${imageHeight}`,
    },
    {
      property: `og:image:alt`,
      content: metaTitle,
    },
  ]

  const mergedMeta: React.DetailedHTMLProps<
    React.MetaHTMLAttributes<HTMLMetaElement>,
    HTMLMetaElement
  >[] = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      name: `title`,
      content: metaTitle,
    },
    {
      property: `og:title`,
      content: metaTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:url`,
      content: canonicalHref,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:site`,
      content: site.siteMetadata?.author || ``,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata?.author || ``,
    },
    {
      name: `twitter:title`,
      content: metaTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: `robots`,
      content: `index, follow, max-image-preview:large`,
    },
    ...imageMeta,
    ...meta,
  ]

  return (
    <Helmet htmlAttributes={{ lang }} title={metaTitle} meta={mergedMeta}>
      {process.env.NODE_ENV === "production" && (
        <link rel="canonical" href={canonicalHref} />
      )}
      {getAlternateLinks(navigatePath, xDefaultPath)}
      <link
        rel="sitemap"
        type="application/xml"
        href="/sitemap-aurora/sitemap-index.xml"
      />
      {isNoIndex && <meta name="robots" content="noindex"></meta>}
      {/* {page === "home" &&
        internalLinks.map((links, index) => (
          <link
            rel="alternate"
            key={index}
            hrefLang={links.hreflang}
            href={links.href}
          />
        ))} */}
      {/* <meta
        httpEquiv="Content-Security-Policy"
        content="default-src 'self' https: wss: data: http: 'unsafe-inline' 'unsafe-eval'"
      ></meta> */}
      {hasFAQSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {customFAQSchema && (
        <script type="application/ld+json">
          {JSON.stringify(customFAQSchema)}
        </script>
      )}
    </Helmet>
  )
}

export default Seo

const query = graphql`
  query {
    site {
      siteMetadata {
        title
        author
      }
    }
  }
`
